import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import type { Quote } from '@client/model';
import {
  benefitPeriodToString,
  formatBenefitPeriod,
  stringToBenefitPeriod,
  type BenefitPeriod,
} from '@client/utils/benefitPeriod';
import type { PaymentOption } from '@client/utils/quotePaymentOption';
import { formatCurrency } from '@shared/formatCurrency';
import { Info, RotateCcw, X } from 'lucide-react';

interface AvailableOptions {
  benefitPeriods: BenefitPeriod[];
  inflationOptions: Array<{ label: string; rate: number }>;
  paymentTypes: PaymentOption[];
}

interface CustomizationPanelProps {
  quote: Quote;
  selectedPremium: number;
  onPremiumChange: (value: number) => void;
  selectedBenefitPeriod: BenefitPeriod;
  onBenefitPeriodChange: (value: BenefitPeriod) => void;
  selectedInflationOption: string;
  onInflationOptionChange: (value: string) => void;
  selectedPaymentType: PaymentOption;
  onPaymentTypeChange: (value: PaymentOption) => void;
  availableOptions: AvailableOptions;
  totalMatchingQuotes: number;
  hasFilters: boolean;
  onResetFilters: () => void;
  sliderBounds: { min: number; max: number };
  onClose: () => void;
}

export function CustomizationPanel({
  quote,
  selectedPremium,
  onPremiumChange,
  selectedBenefitPeriod,
  onBenefitPeriodChange,
  selectedInflationOption,
  onInflationOptionChange,
  selectedPaymentType,
  onPaymentTypeChange,
  availableOptions,
  totalMatchingQuotes,
  hasFilters,
  onResetFilters,
  sliderBounds,
  onClose,
}: CustomizationPanelProps) {
  return (
    <div className="space-y-4 border-t border-gray-200 bg-gray-50 p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Customize Coverage</h3>
        <div className="flex items-center gap-2">
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="text-gray-700 hover:text-gray-900"
            >
              <RotateCcw className="size-4" />
              Reset
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-700 hover:text-gray-900"
          >
            <X className="size-4" />
            Done
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-700">Payment Type</Label>
          <Select
            value={selectedPaymentType}
            onValueChange={onPaymentTypeChange}
          >
            <SelectTrigger className="h-9">
              <SelectValue>{selectedPaymentType}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableOptions.paymentTypes.map(type => (
                <SelectItem
                  key={type}
                  value={type}
                >
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-700">Benefit Period</Label>
          <Select
            value={benefitPeriodToString(selectedBenefitPeriod)}
            onValueChange={value => onBenefitPeriodChange(stringToBenefitPeriod(value))}
          >
            <SelectTrigger className="h-9">
              <SelectValue>{formatBenefitPeriod(selectedBenefitPeriod)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableOptions.benefitPeriods.map(period => (
                <SelectItem
                  key={benefitPeriodToString(period)}
                  value={benefitPeriodToString(period)}
                >
                  {formatBenefitPeriod(period)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-700">Inflation Protection</Label>
          <Select
            value={selectedInflationOption}
            onValueChange={onInflationOptionChange}
          >
            <SelectTrigger className="h-9">
              <SelectValue>{selectedInflationOption}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {availableOptions.inflationOptions.map(option => (
                <SelectItem
                  key={option.label}
                  value={option.label}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-gray-700">Premium Amount</Label>
          <span className="text-primary text-base font-bold">
            {formatCurrency(selectedPremium)}
          </span>
        </div>
        <Slider
          value={[selectedPremium]}
          onValueChange={values => onPremiumChange(values[0])}
          min={sliderBounds.min}
          max={sliderBounds.max}
          step={1000}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-700">
          <span>{formatCurrency(sliderBounds.min)}</span>
          <span>{formatCurrency(sliderBounds.max)}</span>
        </div>
      </div>

      {totalMatchingQuotes === 0 && (
        <Alert variant="destructive">
          <Info className="size-4" />
          <AlertDescription>
            No quotes match these criteria. Try adjusting your filters or reset to defaults.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4 rounded-lg bg-white p-3">
        <div>
          <div className="text-xs text-gray-700">Monthly Benefit</div>
          <div className="text-base font-bold text-gray-900">
            {formatCurrency(Math.ceil(quote.ltcBenefitMonthlyIndividual))}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-700">Total LTC Benefit</div>
          <div className="text-base font-bold text-gray-900">
            {quote.ltcBenefitTotal === 0
              ? 'Unlimited'
              : formatCurrency(Math.ceil(quote.ltcBenefitTotal))}
          </div>
        </div>
      </div>
    </div>
  );
}
